import { applySnapshot, flow, getSnapshot, types } from "mobx-state-tree";
import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";
import api from "services/api";
import { MSURL } from "utils/ms";
import { PUBLISH_STATUSES } from "../constants/models";
import { TagDictionaryItem } from "./dictionary/TagDictionary";

const pathCoach = "/coach/api/v1";
const pathQuiz = "/quiz/api/v1";
const pathBodyGram = "/bodygram/api/v1";
export const ActiveProgram = types.model({
  id: types.number,
  name: types.string,
  value: types.string,
});

export const UserProfileModel = types
  .model({
    id: types.number,
    username: types.string,
    normalizedUsername: types.string,
    email: types.string,
    normalizedEmail: types.string,
    emailConfirmed: types.boolean,
    phoneNumber: types.maybeNull(types.string),
    phoneNumberConfirmed: types.boolean,
    twoFactorEnabled: types.boolean,
    hubspotContactId: types.number,
    pushNotificationEnabled: types.boolean,
    activeProgramId: types.maybeNull(types.number),
    activeProgram: types.maybeNull(ActiveProgram),
    isSubscriptionActive: types.boolean,
    hubpotLastUpdate: types.string,
    isOnboardingComplete: types.boolean,
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
        title: self.email,
      };
    },
  }));

const UserProfileStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`${pathQuiz}/user`, params, undefined, MSURL);
  }),
  initialOrder: { column: '"Id"', desc: true },
  itemModel: UserProfileModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(UserProfileModel),
    isMoreLoading: types.optional(types.boolean, false),
    isUserLoading: types.optional(types.boolean, false),
    isUserManaging: types.optional(types.boolean, false),
    isUserPublishing: types.optional(types.boolean, false),
    isUserCopying: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },
  }))
  .actions(self => ({
    updateProfile(id: number, data: any) {
      const { items } = getSnapshot(self);

      const index = self.items.findIndex(item => item.id === id);
      self.items[index] = { ...items[index], ...data };
    },
  }))
  .actions(self => ({
    fetchMoreUsers: flow(function*() {
      self.isUserLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);
          const { items } = yield api.get(
            `${pathQuiz}/user`,
            {
              size: DEFAULT_PAGE_SIZE,
              page: self.page,
              ...self.filters,
            },
            undefined,
            MSURL,
          );

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isUserLoading = false;
      }
    }),

    fetchUserDetails: flow(function*(id?: number) {
      self.isUserLoading = true;

      try {
        const details = yield api.get(
          `${pathQuiz}/user/${id}`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isUserLoading = false;
      }
    }),

    resetOnboarding: flow(function*(id?: number) {
      self.isUserLoading = true;

      try {
        const details = yield api.put(
          `${pathQuiz}/user/${id}/reset`,
          {},
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isUserLoading = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

export const UserProfileDataStore = types
  .model({
    id: types.maybe(types.number),
    name: types.maybeNull(types.string),
    gender: types.maybeNull(types.number),
    genderText: types.maybeNull(types.string),
    dateOfBirth: types.maybeNull(types.string),
    location: types.maybeNull(types.string),
    phone: types.maybeNull(types.string),
    userId: types.maybe(types.number),
    isLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get formData() {
      let birthDate = "";
      if (self.dateOfBirth) {
        const date = new Date(self.dateOfBirth);
        const dd = String(date.getUTCDate()).padStart(2, "0");
        const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
        const yyyy = date.getUTCFullYear();
        birthDate = mm + "/" + dd + "/" + yyyy;
      }
      return {
        ...self,
        birthDate: birthDate,
      };
    },
  }))
  .actions(self => ({
    fetchProfileData: flow(function*(id?: number) {
      self.isLoading = true;

      try {
        const details = yield api.get(
          `${pathQuiz}/user/${id}/profile`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          ...details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    reset() {
      applySnapshot(self, {});
    },
  }))
  .create();

export const UserProfileMeasurementStore = types
  .model({
    id: types.maybe(types.number),
    bust: types.maybe(types.number),
    waist: types.maybe(types.number),
    hips: types.maybe(types.number),
    butt: types.maybe(types.number),
    rightThigh: types.maybe(types.number),
    leftThigh: types.maybe(types.number),
    rightArm: types.maybe(types.number),
    leftArm: types.maybe(types.number),
    rightCalf: types.maybe(types.number),
    leftCalf: types.maybe(types.number),
    userId: types.maybe(types.number),
    isLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
      };
    },
  }))
  .actions(self => ({
    fetchProfileData: flow(function*(id?: number) {
      self.isLoading = true;

      try {
        const details = yield api.get(
          `${pathQuiz}/user/${id}/measurement`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          ...details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    reset() {
      applySnapshot(self, {});
    },
  }))
  .create();

export const UserProfileFoodPlanStore = types
  .model({
    id: types.maybe(types.number),
    height: types.maybe(types.string),
    heightInCm: types.maybe(types.number),
    weight: types.maybe(types.number),
    weightInKg: types.maybe(types.number),
    userId: types.maybe(types.number),
    isLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
      };
    },
  }))
  .actions(self => ({
    fetchProfileData: flow(function*(id?: number) {
      self.isLoading = true;

      try {
        const details = yield api.get(
          `${pathQuiz}/user/${id}/food-plan`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          ...details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    reset() {
      applySnapshot(self, {});
    },
  }))
  .create();

const QuizArray = types.model("QuizArray", {
  id: types.number,
  created: types.string,
  userId: types.number,
  quizId: types.number,
});

export const UserProfileQuizStore = types
  .model({
    items: types.array(QuizArray),
    isLoading: types.optional(types.boolean, false),
  })
  .actions(self => ({
    fetch: flow(function*(id?: number) {
      try {
        self.isLoading = true;

        const { items } = yield api.get(
          `${pathQuiz}/user/${id}/quiz-response`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          items,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    reset() {
      applySnapshot(self, { items: [] });
    },
  }))
  .create();

const QuizAnswerArray = types.model("QuizAnswerArray", {
  title: types.string,
  answer: types.string,
});

export const UserProfileQuizAnswerStore = types
  .model({
    items: types.array(QuizAnswerArray),
    isLoading: types.optional(types.boolean, false),
  })
  .actions(self => ({
    fetch: flow(function*(userId?: number, quizId?: number) {
      try {
        self.isLoading = true;

        const { items } = yield api.get(
          `${pathQuiz}/user/${userId}/quiz-response/${quizId}`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, { ...self, items });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    reset() {
      applySnapshot(self, { items: [] });
    },
  }))
  .create();

const CoachReportData = types.model("CoachReportData", {
  id: types.maybe(types.number),
  name: types.maybe(types.string),
  email: types.maybe(types.string),
});
export const CoachReport = types
  .model({
    data: types.maybe(CoachReportData),
    isLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
      };
    },
  }))
  .actions(self => ({
    fetchCoachData: flow(function*(id?: number) {
      self.isLoading = true;

      try {
        const details = yield api.get(
          `${pathCoach}/report/user/${id}/coach`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          ...details,
        });

        console.log(self);
        console.log(details);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    reset() {
      applySnapshot(self, {});
    },
  }))
  .create();

export const MeasurementData = types.model({
  unit: types.string,
  value: types.string,
  measurementType: types.string,
});

export const Results = types.model({
  measurements: types.array(MeasurementData),
});

export const Metadata = types.model({
  results: types.maybe(Results),
  clientScanningId: types.string,
  scanningSessionId: types.string,
});

export const BodyGramModel = types
  .model({
    id: types.number,
    scanningSessionId: types.string,
    userId: types.number,
    hasScanned: types.boolean,
    metadata: types.maybe(Metadata),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
      };
    },
  }));

export const BodyGramStore = types
  .model({
    items: types.array(BodyGramModel),
    isLoading: types.optional(types.boolean, false),
  })
  .actions(self => ({
    fetch: flow(function*(userId?: number) {
      try {
        self.isLoading = true;

        const { items } = yield api.get(
          `${pathBodyGram}/bodygram/logs/${userId}`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, { ...self, items });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    reset() {
      applySnapshot(self, { items: [] });
    },
  }))
  .create();

const BodyGramData = types.model("BodygramData", {
  id: types.maybe(types.number),
  scanningSessionId: types.maybe(types.string),
  userId: types.maybe(types.number),
  hasScanned: types.maybe(types.boolean),
  metadata: types.maybe(Metadata),
});
export const BodyGramResult = types
  .model({
    data: types.maybe(BodyGramData),
    isLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
      };
    },
  }))
  .actions(self => ({
    fetchData: flow(function*(userId?: number, id?: number) {
      self.isLoading = true;

      try {
        const details = yield api.get(
          `${pathBodyGram}/bodygram/logs/${userId}/result/${id}`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          ...details,
        });

        console.log(self);
        console.log(details);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    reset() {
      applySnapshot(self, {});
    },
  }))
  .create();

export default UserProfileStore.create();
